import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, FormFeedback, FormGroup, Input, Label, Spinner } from 'reactstrap';
import Select from 'react-select';
import SafeBtn from '../../../shared/SafeBtn';
import { formatRut, validateRut } from '../../../../helpers/rutValidator';
import { validatePhone, isPhoneTooShort } from '../../../../helpers/phoneValidator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { validateIdForCountry, listValidatorsAbbreviationsForCountry } from '../../../../helpers/validateForeignUsersIds';

const RUT_MIN_SIZE = 9;
const FOREIGN_ID_MIN_SIZE = 6;
const PHONE_MIN_SIZE = 7;
const PHONE_MAX_SIZE = 15;

const CreditLineClientForm = ({
  deudor,
  setDeudor,
  handleRutChange,
  handleSaveDeudor,
  savingDeudor,
  clearDeudor,
  deudorStepComplete,
  isUnlock,
  isSiipreloadLoading,
  policyIsExportacion,
  paises,
  onPaisChange,
}) => {
  const [validRut, setValidRut] = useState(true);
  const [invalidRutMsg, setInvalidRutMsg] = useState('El RUT ingresado es inválido');
  const [validPhone, setValidPhone] = useState(true);
  const [phoneTooShort, setPhoneTooShort] = useState(false);
  const [dniValidators, setDniValidators] = useState([])

  const paisesOptions = paises
    .filter(p => p.code !== 'CL')
    .map(p => ({
      value: p.id,
      label: p.name,
      code: p.code,
    }));

  const selectedPais = deudor.pais
    ? paisesOptions.find(opt => opt.value === deudor.pais)
    : null;

  const hasNoValidator = policyIsExportacion && !!selectedPais?.code && dniValidators.length === 0;

  useEffect(() => {
    if (!selectedPais?.code) return;

    const validators = listValidatorsAbbreviationsForCountry(selectedPais.code);

    if (Array.isArray(validators) && validators.length > 0) {
      setDniValidators(validators);
    } else {
      setDniValidators([]);
    }
  }, [selectedPais?.code]);

  useEffect(() => {
    if (!policyIsExportacion || selectedPais?.code === 'CL') {
      if (deudor.rut.length > RUT_MIN_SIZE ||
          (deudor.rut.length === RUT_MIN_SIZE && parseInt(deudor.rut.substring(0, 2), 10) > 25)) {
        const validatedRut = validateRut(deudor.rut);

        if (!validateRut.isValid) {
          setInvalidRutMsg('El RUT ingresado es inválido');
        }

        setValidRut(validatedRut.isValid);
      } else {
        setValidRut(false);
      }
    } else if (policyIsExportacion && selectedPais?.code) {
      if (dniValidators.length === 0) {
        setValidRut(true);
      } else if (deudor.rut.length >= FOREIGN_ID_MIN_SIZE) {
        const validationResult = validateIdForCountry(selectedPais.code, deudor.rut);
        if (!validationResult.ok) {
          setInvalidRutMsg(`El identificador ingresado no es ${validationResult.triedStr}`);
        }

        setValidRut(validationResult.ok);
      }
    }
  }, [deudor.rut, policyIsExportacion, selectedPais?.code, dniValidators]);

  const buttonText = deudorStepComplete ? "Editar" : "Continuar";

  useEffect(() => {
    if (deudor.phone.length === 0) {
      setValidPhone(true);
      setPhoneTooShort(false);
    } else {
      const isValid = validatePhone(deudor.phone);
      setValidPhone(isValid);
      const isTooShort = isPhoneTooShort(deudor.phone, PHONE_MIN_SIZE);
      setPhoneTooShort(isTooShort);
    }
  }, [deudor.phone]);


  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleSaveDeudor();
      }}
    >
      {policyIsExportacion && (
        <FormGroup row>
          <Label for="pais" sm={4}>
            País
          </Label>
          <Col sm={8}>
            <Select
              id="pais"
              className="orsan-form-element"
              options={paisesOptions}
              value={selectedPais}
              onChange={(opt) => onPaisChange(opt ? opt.value : null)}
              placeholder="Seleccione país"
              isClearable
              isSearchable
              isDisabled={isUnlock}
              noOptionsMessage={() => "Sin resultados"}
            />
          </Col>
        </FormGroup>
      )}
      <FormGroup row>
        <Label for="rut" sm={4}>
          {policyIsExportacion ? "N° Documento" : "RUT"}
        </Label>
        <Col sm={8}>
          <Input
            id="rut"
            className="orsan-form-element"
            value={
              !policyIsExportacion || selectedPais?.code === 'CL'
                ? (formatRut(deudor.rut))
                : (deudor.rut)
            }
            onChange={(e) => handleRutChange(e.target.value)}
            invalid={deudor.rut.length >= RUT_MIN_SIZE && !validRut}
            disabled={isUnlock || (policyIsExportacion && !deudor.pais)}
            placeholder={
              policyIsExportacion &&
              selectedPais?.code &&
              !hasNoValidator &&
              `Ej: ${dniValidators.join(' / ')}`
            }
          />
          {((!policyIsExportacion && deudor.rut.length >= RUT_MIN_SIZE) ||
            (policyIsExportacion && deudor.rut.length >= FOREIGN_ID_MIN_SIZE)) &&
            !validRut && <FormFeedback>{invalidRutMsg}</FormFeedback>}
          {hasNoValidator && deudor.rut.length >= FOREIGN_ID_MIN_SIZE && (
            <small className="form-text mt-1" style={{ color: 'var(--orsan-dark-grey)' }}>
              No contamos con un validador para este país. Por favor, verifique que el N° Documento ingresado sea correcto antes de continuar.
            </small>
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="name" sm={4}>
          Nombre
        </Label>
        <Col sm={8}>
          <div style={{ position: 'relative' }}>
            <Input
              id="name"
              className="orsan-form-element"
              value={deudor.name}
              onChange={(e) => setDeudor('name', e.target.value)}
              disabled={
                !validRut || 
                deudor.rut.length < (hasNoValidator ? FOREIGN_ID_MIN_SIZE : RUT_MIN_SIZE) || 
                isUnlock || 
                (!policyIsExportacion && isSiipreloadLoading)  // Only wait for SII on domestic policies
              }
            />
            {!policyIsExportacion && isSiipreloadLoading && validRut && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}>
                <Spinner size="sm" color="primary" />
              </div>
            )}
          </div>
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="address" sm={4}>
          Dirección
        </Label>
        <Col sm={8}>
          <Input
            id="address"
            className="orsan-form-element"
            value={deudor.address}
            onChange={(e) => setDeudor('address', e.target.value)}
            disabled={!validRut || deudor.rut.length < (hasNoValidator ? FOREIGN_ID_MIN_SIZE : RUT_MIN_SIZE) || isUnlock}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="email" sm={4}>
          Correo Electrónico
        </Label>
        <Col sm={8}>
          <Input
            type="email"
            id="email"
            className="orsan-form-element"
            value={deudor.email}
            onChange={(e) => setDeudor('email', e.target.value)}
            disabled={!validRut || deudor.rut.length < (hasNoValidator ? FOREIGN_ID_MIN_SIZE : RUT_MIN_SIZE) || isUnlock}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="phone" sm={4}>
          Teléfono
        </Label>
        <Col sm={8}>
          <Input
            id="phone"
            className="orsan-form-element"
            value={deudor.phone}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(/[^0-9+\s]/g, '');
              if (sanitizedValue.length <= PHONE_MAX_SIZE) {
                setDeudor('phone', sanitizedValue);
              }
            }}
            invalid={!validPhone || phoneTooShort}
            disabled={!validRut || deudor.rut.length < (hasNoValidator ? FOREIGN_ID_MIN_SIZE : RUT_MIN_SIZE) || isUnlock}
          />
          {!validPhone && (
            <FormFeedback>El teléfono solo puede contener números, "+" o espacios.</FormFeedback>
          )}
          {phoneTooShort && (
            <FormFeedback>El teléfono debe tener al menos {PHONE_MIN_SIZE} dígitos.</FormFeedback>
          )}
        </Col>
      </FormGroup>
      {isUnlock && (
        <FormFeedback className="error-msg mb-3 d-flex justify-content-end align-items-center" style={{ fontSize: '14px' }}><FontAwesomeIcon icon={faInfoCircle} className="mr-1"/>Este deudor se encuentra bloqueado.</FormFeedback>
      )}
      <div className="mb-3 d-flex justify-content-end">
        <Button type="button" onClick={clearDeudor} color="orsan-link" disabled={isUnlock}>
          Limpiar
        </Button>
        <SafeBtn
          color="orsan-secondary"
          className="ml-sm-2"
          loading={savingDeudor}
          disabled={!validRut || !deudor.name || !validPhone || phoneTooShort}
        >
          {buttonText}
        </SafeBtn>
      </div>
    </Form>
  );
};

CreditLineClientForm.propTypes = {
  deudor: PropTypes.shape({
    rut: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    pais: PropTypes.number,
  }).isRequired,
  setDeudor: PropTypes.func.isRequired,
  handleRutChange: PropTypes.func.isRequired,
  handleSaveDeudor: PropTypes.func.isRequired,
  savingDeudor: PropTypes.bool.isRequired,
  clearDeudor: PropTypes.func.isRequired,
  deudorStepComplete: PropTypes.bool.isRequired,
  isUnlock: PropTypes.bool,
  isSiipreloadLoading: PropTypes.bool,
  policyIsExportacion: PropTypes.bool.isRequired,
  paises: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      code: PropTypes.string,
    }),
  ),
  onPaisChange: PropTypes.func,
};

CreditLineClientForm.defaultProps = {
  isUnlock: false,
  isSiipreloadLoading: false,
  paises: [],
  onPaisChange: () => {},
}

export default CreditLineClientForm;
